import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  RefreshCw,
  Play,
  Pause,
  FolderOpen,
  Sparkles,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Check,
  ImageIcon,
} from "lucide-react";
import {
  listSharePointImageFolders,
  searchSharePointImages,
  getThumbnailUrl,
  getAltTextConfig,
  generateImageAltText,
  type SharePointImageIndexRow,
  type AltTextConfig,
} from "@/lib/sharepoint-api";
import { SharePointLayout } from "./layout";

function formatDate(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function getFolderUrl(webUrl?: string | null): string | null {
  if (!webUrl) return null;
  const lastSlash = webUrl.lastIndexOf('/');
  return lastSlash > 0 ? webUrl.substring(0, lastSlash) : null;
}

function getConfidenceBadge(confidence?: number | null) {
  if (confidence === null || confidence === undefined) return null;
  if (confidence >= 0.8) return <Badge variant="default" className="bg-green-600">High</Badge>;
  if (confidence >= 0.5) return <Badge variant="secondary">Medium</Badge>;
  return <Badge variant="outline">Low</Badge>;
}

// Thumbnail component with lazy loading and error handling
function ImageThumbnail({ siteId, driveId, itemId }: { siteId: string; driveId: string; itemId: string }) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const url = getThumbnailUrl(siteId, driveId, itemId);

  if (error) {
    return (
      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded">
        <ImageIcon className="w-5 h-5 text-gray-400" />
      </div>
    );
  }

  return (
    <div className="w-12 h-12 relative">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      )}
      <img
        src={url}
        alt="thumbnail"
        className={`w-12 h-12 object-cover rounded ${loaded ? '' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        loading="lazy"
      />
    </div>
  );
}

// Alt text dialog component
function AltTextDialog({
  image,
  open,
  onOpenChange,
  config,
}: {
  image: SharePointImageIndexRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config?: AltTextConfig;
}) {
  const queryClient = useQueryClient();
  const [selectedModel, setSelectedModel] = useState(config?.defaultModel || "");
  const [customPrompt, setCustomPrompt] = useState("");
  const [updateSharePoint, setUpdateSharePoint] = useState(false);
  const [result, setResult] = useState<{ success?: boolean; altText?: string; confidence?: number; tokensUsed?: number; error?: string } | null>(null);

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!image) throw new Error("No image selected");
      return generateImageAltText(image.SiteId, image.DriveId, image.ItemId, {
        model: selectedModel || undefined,
        prompt: customPrompt || undefined,
        updateSharePoint,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ["sharepoint-images"] });
      }
    },
  });

  // Reset state when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setResult(null);
      setCustomPrompt("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Generate Alt Text</DialogTitle>
          <DialogDescription>
            {image?.FileName ? `Generate accessibility text for: ${image.FileName}` : "Generate accessibility text for this image"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Thumbnail preview */}
          {image && (
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <ImageThumbnail siteId={image.SiteId} driveId={image.DriveId} itemId={image.ItemId} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{image.FileName}</div>
                <div className="text-sm text-muted-foreground truncate">{image.FilePath}</div>
                {image.Width && image.Height && (
                  <div className="text-sm text-muted-foreground">
                    {image.Width} x {image.Height} {image.DpiX ? `@ ${image.DpiX} DPI` : ""}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Existing alt text */}
          {image?.AltText && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-sm font-medium text-blue-900 mb-1">Current Alt Text:</div>
              <div className="text-sm text-blue-800">{image.AltText}</div>
              {image.AltTextConfidence !== null && image.AltTextConfidence !== undefined && (
                <div className="mt-2 flex items-center gap-2">
                  {getConfidenceBadge(image.AltTextConfidence)}
                  <span className="text-xs text-blue-600">
                    Generated by {image.AltTextModel} on {formatDate(image.AltTextGeneratedDate)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Model selection */}
          <div className="space-y-2">
            <Label>AI Model</Label>
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select a model..." />
              </SelectTrigger>
              <SelectContent>
                {config?.models.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex flex-col">
                      <span>{model.name}</span>
                      <span className="text-xs text-muted-foreground">{model.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom prompt */}
          <div className="space-y-2">
            <Label>Custom Prompt (optional)</Label>
            <Textarea
              placeholder={config?.defaultPrompt || "Enter custom instructions for the AI..."}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use the default prompt
            </p>
          </div>

          {/* Update SharePoint checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="updateSharePoint"
              checked={updateSharePoint}
              onChange={(e) => setUpdateSharePoint(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="updateSharePoint" className="font-normal">
              Also update the AltText column in SharePoint
            </Label>
          </div>

          {/* Result display */}
          {result && (
            <div className={`p-3 rounded-lg ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
              {result.success ? (
                <>
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                    <Check className="w-4 h-4" />
                    Generated Alt Text:
                  </div>
                  <div className="text-sm text-green-800">{result.altText}</div>
                  <div className="mt-2 flex items-center gap-2">
                    {getConfidenceBadge(result.confidence)}
                    <span className="text-xs text-green-600">
                      {result.tokensUsed} tokens used
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span>Error: {result.error}</span>
                </div>
              )}
            </div>
          )}

          {/* Generate button */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {image?.AltText ? "Regenerate" : "Generate"}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SharePointImagesPage() {
  const [folderFilter, setFolderFilter] = useState("");
  const [fileFilter, setFileFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [altTextFilter, setAltTextFilter] = useState<string>("all");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedImage, setSelectedImage] = useState<SharePointImageIndexRow | null>(null);
  const [altTextDialogOpen, setAltTextDialogOpen] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

  const queryParams = useMemo(
    () => ({
      folder: folderFilter || undefined,
      fileName: fileFilter || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      hasAltText: altTextFilter === "all" ? undefined : altTextFilter === "has",
      limit: pageSize,
      offset: currentPage * pageSize,
    }),
    [folderFilter, fileFilter, statusFilter, altTextFilter, pageSize, currentPage]
  );

  const foldersQuery = useQuery({
    queryKey: ["sharepoint-image-folders"],
    queryFn: () => listSharePointImageFolders({ limit: 200 }),
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const imagesQuery = useQuery({
    queryKey: ["sharepoint-images", queryParams],
    queryFn: () => searchSharePointImages(queryParams),
    refetchInterval: autoRefresh ? 3000 : false,
  });

  const altTextConfigQuery = useQuery({
    queryKey: ["alt-text-config"],
    queryFn: getAltTextConfig,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const totalPages = Math.ceil((imagesQuery.data?.total || 0) / pageSize);

  const toggleError = (key: string) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const openAltTextDialog = (image: SharePointImageIndexRow) => {
    setSelectedImage(image);
    setAltTextDialogOpen(true);
  };

  // Reset page when filters change
  const handleFilterChange = (setter: (value: string) => void, value: string) => {
    setter(value);
    setCurrentPage(0);
  };

  return (
    <SharePointLayout>
      <TooltipProvider>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-1">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Folders</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => foldersQuery.refetch()}
                disabled={foldersQuery.isFetching}
              >
                <RefreshCw className={`h-4 w-4 ${foldersQuery.isFetching ? "animate-spin" : ""}`} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {foldersQuery.data?.folders?.length ? (
                <div className="max-h-[65vh] overflow-auto space-y-1">
                  {foldersQuery.data.folders.map((f) => (
                    <button
                      key={f.FolderPath}
                      className="w-full text-left rounded border bg-white px-2 py-1 hover:bg-gray-50"
                      onClick={() => handleFilterChange(setFolderFilter, f.FolderPath)}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm">{f.FolderPath}</span>
                        <Badge variant="secondary">{f.imageCount}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Last activity: {formatDate(f.lastActivityUtc)}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {foldersQuery.isLoading ? "Loading..." : "No folders indexed yet."}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-3">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Indexed Images</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant={autoRefresh ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  title={autoRefresh ? "Stop auto-refresh" : "Start auto-refresh (3s)"}
                >
                  {autoRefresh ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  <span className="ml-1 text-xs">{autoRefresh ? "Live" : "Auto"}</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => imagesQuery.refetch()}
                  disabled={imagesQuery.isFetching}
                >
                  <RefreshCw className={`h-4 w-4 ${imagesQuery.isFetching ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <Input
                  placeholder="Folder contains..."
                  value={folderFilter}
                  onChange={(e) => handleFilterChange(setFolderFilter, e.target.value)}
                />
                <Input
                  placeholder="Filename contains..."
                  value={fileFilter}
                  onChange={(e) => handleFilterChange(setFileFilter, e.target.value)}
                />
                <Select value={statusFilter} onValueChange={(v) => handleFilterChange(setStatusFilter, v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Queued">Queued</SelectItem>
                    <SelectItem value="Processing">Processing</SelectItem>
                    <SelectItem value="Success">Success</SelectItem>
                    <SelectItem value="Failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={altTextFilter} onValueChange={(v) => handleFilterChange(setAltTextFilter, v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Alt Text" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Images</SelectItem>
                    <SelectItem value="has">Has Alt Text</SelectItem>
                    <SelectItem value="missing">Missing Alt Text</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFolderFilter("");
                    setFileFilter("");
                    setStatusFilter("all");
                    setAltTextFilter("all");
                    setCurrentPage(0);
                  }}
                  className="text-muted-foreground"
                >
                  Clear Filters
                </Button>
              </div>

              {/* Status bar */}
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {imagesQuery.data
                    ? `Showing ${currentPage * pageSize + 1}-${Math.min((currentPage + 1) * pageSize, imagesQuery.data.total)} of ${imagesQuery.data.total}`
                    : imagesQuery.isLoading
                      ? "Loading..."
                      : ""}
                </span>
                <div className="flex items-center gap-4">
                  {autoRefresh && (
                    <span className="flex items-center gap-1 text-green-600">
                      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                      Auto-refreshing
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <span>Page size:</span>
                    <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(0); }}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="rounded border bg-white overflow-auto max-h-[60vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Preview</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Folder</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Meta</TableHead>
                      <TableHead>Alt Text</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(imagesQuery.data?.items || []).map((row) => {
                      const key = `${row.SiteId}|${row.DriveId}|${row.ItemId}`;
                      const hasError = row.Status === "Failed" && row.ErrorMessage;
                      const isExpanded = expandedErrors.has(key);

                      return (
                        <TableRow key={key}>
                          <TableCell>
                            <ImageThumbnail siteId={row.SiteId} driveId={row.DriveId} itemId={row.ItemId} />
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {row.WebUrl ? (
                              <a
                                href={row.WebUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate underline underline-offset-2 block"
                                title={row.WebUrl}
                              >
                                {row.FileName}
                              </a>
                            ) : (
                              <div className="truncate">{row.FileName}</div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              {formatDate(row.ProcessedAtUtc)}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[180px]">
                            <div className="flex items-center gap-1">
                              {getFolderUrl(row.WebUrl) && (
                                <a
                                  href={getFolderUrl(row.WebUrl)!}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                                  title="Open folder in SharePoint"
                                >
                                  <FolderOpen className="h-4 w-4" />
                                </a>
                              )}
                              <span className="truncate text-sm" title={row.FolderPath || undefined}>
                                {row.FolderPath || "-"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Badge
                                variant={
                                  row.Status === "Failed"
                                    ? "destructive"
                                    : row.Status === "Queued"
                                      ? "outline"
                                      : row.Status === "Success"
                                        ? "default"
                                        : "secondary"
                                }
                                className={row.Status === "Success" ? "bg-green-600" : ""}
                              >
                                {row.Status}
                              </Badge>
                              {hasError && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => toggleError(key)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <AlertCircle className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-sm">
                                    <p className="text-xs">{row.ErrorMessage}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            {isExpanded && hasError && (
                              <div className="mt-1 text-xs text-red-600 bg-red-50 p-1 rounded max-w-[200px] break-words">
                                {row.ErrorMessage}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.Width && row.Height ? (
                              <div>
                                <div>{row.Width} x {row.Height}</div>
                                {row.DpiX && <div className="text-muted-foreground">{row.DpiX} DPI</div>}
                              </div>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {row.AltText ? (
                              <div className="space-y-1">
                                <div className="text-xs truncate" title={row.AltText}>
                                  {row.AltText}
                                </div>
                                <div className="flex items-center gap-1">
                                  {getConfidenceBadge(row.AltTextConfidence)}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2"
                                    onClick={() => openAltTextDialog(row)}
                                  >
                                    <Sparkles className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7"
                                onClick={() => openAltTextDialog(row)}
                              >
                                <Sparkles className="h-3 w-3 mr-1" />
                                Generate
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!imagesQuery.isLoading && !(imagesQuery.data?.items || []).length ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-sm text-muted-foreground text-center py-8">
                          No results found.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-2 text-sm">
                      {currentPage + 1} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage >= totalPages - 1}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alt Text Dialog */}
        <AltTextDialog
          image={selectedImage}
          open={altTextDialogOpen}
          onOpenChange={setAltTextDialogOpen}
          config={altTextConfigQuery.data}
        />
      </TooltipProvider>
    </SharePointLayout>
  );
}
